import type { CustomerType } from '@/stores/customer-type';
import type { Currency } from '@/lib/locale';

/**
 * Returns the display price based on customer type.
 * DB prices are inkl. 25% moms. Business customers see exkl. moms.
 */
export function getDisplayPrice(price: number, customerType: CustomerType): number {
  if (customerType === 'business') {
    return price / 1.25;
  }
  return price;
}

/**
 * Formats a price in the given currency.
 * SEK: "1 234 kr" (Swedish locale, no decimals)
 * EUR: "€77.00" (English locale, 2 decimals)
 */
export function formatPrice(price: number, currency: Currency = 'SEK'): string {
  if (currency === 'EUR') {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Returns the VAT label for display.
 */
export function getVatLabel(customerType: CustomerType): string {
  return customerType === 'business' ? 'Exkl. moms' : 'Inkl. moms';
}

/**
 * Returns the VAT amount (25%) for a given price (inkl. moms).
 */
export function getVatAmount(price: number): number {
  return price - price / 1.25;
}

/**
 * Returns the B2B discounted price (exkl. moms, after discount).
 * DB price is inkl. 25% moms → exkl. moms → apply discount.
 */
export function getBusinessPrice(priceInclMoms: number, discountPercent: number): number {
  const exklMoms = priceInclMoms / 1.25;
  return exklMoms * (1 - discountPercent / 100);
}

/**
 * Convert a SEK price (inkl. 25% moms) to EUR.
 * Steps: Remove 25% VAT → apply markup → divide by exchange rate.
 *
 * @param sekPriceInclVat - Price in SEK including 25% VAT
 * @param exchangeRate - SEK per EUR (e.g. 11.50)
 * @param markup - Markup multiplier (e.g. 1.10 for 10%)
 * @returns Price in EUR
 */
export function convertToEUR(sekPriceInclVat: number, exchangeRate: number, markup: number = 1.10): number {
  const exklVat = sekPriceInclVat / 1.25;
  const withMarkup = exklVat * markup;
  return withMarkup / exchangeRate;
}
